'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Button,
  ConfirmationDialog,
  IconButton,
  Input,
  LabelDefault,
  Menu,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import {
  ChevronsDownUpIcon,
  ArrowUpDownIcon,
  FolderAddIcon,
  PanelLeftCloseIcon,
} from '@terra/ui-icons';
import {
  useReplaySessionStore,
  type ReplaySession,
} from '../../store/replaySessionStore';
import { useTranslations } from 'next-intl';
import { ReplayCollectionGroup } from './ReplayCollectionGroup';
import { ReplaySessionRow } from './ReplaySessionRow';

type ReplaySessionGroupsProps = {
  sessions: ReplaySession[];
  activeSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onCloseSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, name: string) => void;
  onMinimize?: () => void;
};
type EditingState = {
  sessionId: string;
  title: string;
} | null;

export function ReplaySessionGroups({
  sessions,
  activeSessionId,
  onSelectSession,
  onCloseSession,
  onRenameSession,
  onMinimize,
}: ReplaySessionGroupsProps) {
  const tCommon = useTranslations('common');
  const tCollections = useTranslations('replay.collections');
  const {
    collections,
    activeCollectionId,
    collapsedCollectionIds,
    setActiveCollection,
    createCollection,
    renameCollection,
    deleteCollection,
    moveSessionToCollection,
    reorderCollections,
    toggleCollectionCollapsed,
    collapseAllCollections,
    expandAllCollections,
    isCollectionNameTaken,
  } = useReplaySessionStore();
  const allCollapsed =
    collections.length > 0 &&
    collections.every((collection) =>
      collapsedCollectionIds.includes(collection.id),
    );
  const [editing, setEditing] = useState<EditingState>(null);
  const [hoveredCollectionId, setHoveredCollectionId] = useState<string | null>(
    null,
  );
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [draggedSessionId, setDraggedSessionId] = useState<string | null>(null);
  const [dragOverCollectionId, setDragOverCollectionId] = useState<
    string | null
  >(null);
  const [dragOverSessionId, setDragOverSessionId] = useState<string | null>(
    null,
  );
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [renamingCollectionId, setRenamingCollectionId] = useState<
    string | null
  >(null);
  const [renamingCollectionName, setRenamingCollectionName] = useState('');
  const [collectionPendingDeletion, setCollectionPendingDeletion] = useState<
    (typeof collections)[number] | null
  >(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const sessionEditInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!renamingCollectionId) return;
    const frame = requestAnimationFrame(() => {
      const input = renameInputRef.current;

      if (input) {
        input.focus();
        input.select();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [renamingCollectionId]);
  const editingSessionId = editing?.sessionId ?? null;

  useEffect(() => {
    if (!editingSessionId) return;
    const frame = requestAnimationFrame(() => {
      const input = sessionEditInputRef.current;

      if (input) {
        input.focus();
        input.select();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [editingSessionId]);
  const sessionsByCollection = useMemo(() => {
    const grouped = new Map<string, ReplaySession[]>();

    for (const session of sessions) {
      const collectionSessions = grouped.get(session.collectionId) ?? [];

      collectionSessions.push(session);
      grouped.set(session.collectionId, collectionSessions);
    }

    return grouped;
  }, [sessions]);
  const saveSessionTitle = () => {
    if (!editing) return;
    const trimmed = editing.title.trim();

    if (trimmed) {
      onRenameSession(editing.sessionId, trimmed);
    }

    setEditing(null);
  };

  const startRenameCollection = (collectionId: string, name: string) => {
    setRenamingCollectionId(collectionId);
    setRenamingCollectionName(name);
  };

  const renamingCollection = collections.find(
    (collection) => collection.id === renamingCollectionId,
  );
  const renameTrimmed = renamingCollectionName.trim();
  const isRenameUnchanged = renamingCollection?.name.trim() === renameTrimmed;
  const isRenameDuplicate =
    !!renameTrimmed &&
    !isRenameUnchanged &&
    !!renamingCollectionId &&
    isCollectionNameTaken(renameTrimmed, renamingCollectionId);
  const cancelRenameCollection = () => {
    setRenamingCollectionId(null);
    setRenamingCollectionName('');
  };

  const submitRenameCollection = () => {
    if (!renamingCollectionId) return;
    if (!renameTrimmed) {
      cancelRenameCollection();

      return;
    }

    if (isRenameUnchanged) {
      cancelRenameCollection();

      return;
    }

    if (isRenameDuplicate) {
      return;
    }

    renameCollection(renamingCollectionId, renameTrimmed);
    cancelRenameCollection();
  };

  const newCollectionTrimmed = newCollectionName.trim();
  const isNewCollectionDuplicate =
    !!newCollectionTrimmed && isCollectionNameTaken(newCollectionTrimmed);
  const createNewCollection = () => {
    if (!newCollectionTrimmed || isNewCollectionDuplicate) {
      return;
    }

    const created = createCollection(newCollectionTrimmed);

    if (!created) {
      return;
    }

    setNewCollectionName('');
    setIsCreatingCollection(false);
  };

  const clearDragState = () => {
    setDraggedSessionId(null);
    setDragOverCollectionId(null);
    setDragOverSessionId(null);
  };

  const handleConfirmDeleteCollection = () => {
    if (!collectionPendingDeletion) return;
    deleteCollection(collectionPendingDeletion.id);
    setCollectionPendingDeletion(null);
  };

  return (
    <>
      <Box
        h="full"
        w="full"
        minW="0"
        borderRightWidth="1px"
        borderColor="border.subtle"
        borderRadius="0"
        bg="bg.subtle"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <HStack
          px="2"
          py="1.5"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          gap="1"
        >
          <LabelDefault fontWeight="medium" color="fg.default" flex="1">
            {tCollections('title')}
          </LabelDefault>
          <Menu.Root
            open={isCreatingCollection}
            onOpenChange={({ open }) => setIsCreatingCollection(open)}
            positioning={{ strategy: 'fixed' }}
          >
            <Menu.Trigger asChild>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={tCollections('createCollection')}
                color="fg.muted"
              >
                <FolderAddIcon size={14} />
              </IconButton>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content minW="180px" py="1">
                <VStack p="1.5" gap="1.5" alignItems="stretch">
                  <MetaDefault fontWeight="medium">
                    {tCollections('collectionName')}
                  </MetaDefault>
                  <Input
                    size="xs"
                    value={newCollectionName}
                    onChange={(event) =>
                      setNewCollectionName(event.target.value)
                    }
                    placeholder={tCollections('collectionNamePlaceholder')}
                    autoFocus
                    borderColor={
                      isNewCollectionDuplicate ? 'border.error' : undefined
                    }
                    aria-invalid={isNewCollectionDuplicate || undefined}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        createNewCollection();
                      }

                      if (event.key === 'Escape') {
                        setIsCreatingCollection(false);
                      }
                    }}
                  />
                  {isNewCollectionDuplicate && (
                    <MetaDefault color="fg.error">
                      {tCollections('nameAlreadyTaken')}
                    </MetaDefault>
                  )}
                  <HStack gap="1.5" justifyContent="flex-end">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => setIsCreatingCollection(false)}
                    >
                      {tCommon('cancel')}
                    </Button>
                    <Button
                      variant="solid"
                      size="xs"
                      disabled={
                        !newCollectionTrimmed || isNewCollectionDuplicate
                      }
                      onClick={createNewCollection}
                    >
                      {tCollections('create')}
                    </Button>
                  </HStack>
                </VStack>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
          {collections.length > 0 && (
            <Tooltip
              title={
                allCollapsed
                  ? tCollections('expandAllCollections')
                  : tCollections('collapseAllCollections')
              }
            >
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={
                  allCollapsed
                    ? tCollections('expandAllCollections')
                    : tCollections('collapseAllCollections')
                }
                color="fg.muted"
                onClick={
                  allCollapsed ? expandAllCollections : collapseAllCollections
                }
              >
                {allCollapsed ? (
                  <ArrowUpDownIcon size={14} />
                ) : (
                  <ChevronsDownUpIcon size={14} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {onMinimize && (
            <Tooltip title={tCollections('collapsePanel')}>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={tCollections('collapsePanel')}
                color="fg.muted"
                onClick={onMinimize}
              >
                <PanelLeftCloseIcon size={14} />
              </IconButton>
            </Tooltip>
          )}
        </HStack>

        <VStack
          gap="1"
          alignItems="stretch"
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          py="1"
          css={{
            scrollbarWidth: 'thin',
          }}
        >
          {collections.map((collection) => {
            const collectionSessions =
              sessionsByCollection.get(collection.id) ?? [];

            return (
              <ReplayCollectionGroup
                key={collection.id}
                collection={collection}
                sessionCount={collectionSessions.length}
                isCollapsed={collapsedCollectionIds.includes(collection.id)}
                isActive={activeCollectionId === collection.id}
                isHovered={hoveredCollectionId === collection.id}
                isRenaming={renamingCollectionId === collection.id}
                isDragOver={dragOverCollectionId === collection.id}
                draggedSessionId={draggedSessionId}
                isRenameDuplicate={
                  renamingCollectionId === collection.id && isRenameDuplicate
                }
                renamingCollectionName={renamingCollectionName}
                renameInputRef={renameInputRef}
                onHoverChange={setHoveredCollectionId}
                onDragOver={() => setDragOverCollectionId(collection.id)}
                onDragLeave={() => setDragOverCollectionId(null)}
                onDragEnd={clearDragState}
                onDrop={({ sessionId, collectionId }) => {
                  moveSessionToCollection(sessionId, collectionId);
                  setActiveCollection(collectionId);
                  clearDragState();
                }}
                onReorderCollections={({
                  collectionId,
                  beforeCollectionId,
                }) => {
                  reorderCollections(collectionId, beforeCollectionId);
                  clearDragState();
                }}
                onSelect={() => setActiveCollection(collection.id)}
                onToggleCollapsed={() =>
                  toggleCollectionCollapsed(collection.id)
                }
                onRenamingNameChange={setRenamingCollectionName}
                onSubmitRename={submitRenameCollection}
                onCancelRename={cancelRenameCollection}
                onStartRename={() =>
                  startRenameCollection(collection.id, collection.name)
                }
                onRequestDelete={() => setCollectionPendingDeletion(collection)}
              >
                {collectionSessions.map((session) => (
                  <ReplaySessionRow
                    key={session.id}
                    session={session}
                    collections={collections}
                    isEditing={editing?.sessionId === session.id}
                    editingTitle={
                      editing?.sessionId === session.id
                        ? editing.title
                        : session.title
                    }
                    isActive={activeSessionId === session.id}
                    isHovered={hoveredSessionId === session.id}
                    isDragged={draggedSessionId === session.id}
                    isDropTarget={dragOverSessionId === session.id}
                    sessionEditInputRef={sessionEditInputRef}
                    onHoverChange={setHoveredSessionId}
                    onDragStart={setDraggedSessionId}
                    onDragEnd={clearDragState}
                    onDropOnSession={(beforeSessionId) => {
                      const sessionId = draggedSessionId;

                      if (!sessionId || sessionId === beforeSessionId) {
                        clearDragState();

                        return;
                      }

                      moveSessionToCollection(
                        sessionId,
                        session.collectionId,
                        beforeSessionId,
                      );
                      setActiveCollection(session.collectionId);
                      clearDragState();
                    }}
                    onDropCollection={(draggedCollectionId) => {
                      reorderCollections(
                        draggedCollectionId,
                        session.collectionId,
                      );
                      clearDragState();
                    }}
                    onDropTargetChange={setDragOverSessionId}
                    onSelect={() => {
                      setActiveCollection(session.collectionId);
                      onSelectSession(session.id);
                    }}
                    onClose={() => onCloseSession(session.id)}
                    onEditingTitleChange={(title) =>
                      setEditing({ sessionId: session.id, title })
                    }
                    onSaveTitle={saveSessionTitle}
                    onCancelEdit={() => setEditing(null)}
                    onStartRename={() =>
                      setEditing({
                        sessionId: session.id,
                        title: session.title,
                      })
                    }
                    onMoveToCollection={(collectionId) =>
                      moveSessionToCollection(session.id, collectionId)
                    }
                  />
                ))}
              </ReplayCollectionGroup>
            );
          })}
        </VStack>
      </Box>
      <ConfirmationDialog
        isOpen={collectionPendingDeletion !== null}
        onOpenChange={({ open }) => {
          if (!open) setCollectionPendingDeletion(null);
        }}
        onConfirm={handleConfirmDeleteCollection}
        variant="destructive"
        title={tCollections('deleteCollection')}
        description={
          collectionPendingDeletion
            ? tCollections('deleteCollectionDescription', {
                name: collectionPendingDeletion.name,
              })
            : ''
        }
        confirmText={tCommon('delete')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
}
